import React, { useEffect, useState } from 'react';
import { enhanceGLBridge } from '@meitu/wasm-lib';
import {
    checkPointInStage,
    convertToLayerPoint,
    getFramework,
    useGlobalMask,
    useIdentity,
    useScale,
} from '@/framework';
import { convertMousePointToLayerPoint } from '@/layers/main-layer/utils';
import { isLeftButton } from '@/utils/event-kit';
import { IPoint } from '@/version-types';
import { IntelligentColorPlugin } from '../controller';
import { useIntelligentColorState } from '../states/intelligent-color-state';
import './color-pick-pen.less';

let glCanvas: HTMLCanvasElement | null = null;

const DEFAULT_COLOR = '0,0,0,1';

export const MAX_COLOR_LENGTH = 6;

export const ColorPickPen = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const stageIndex = framework.stage.getActiveStageIndex();
    const originIndex = framework.stage.getActiveStageIndex();

    const scale = useScale();
    const globalMask = useGlobalMask();
    const isShowLoading = globalMask !== null;

    const penSize = useIntelligentColorState()((state) => state.intelligentPenSize);
    const replaceIntelligentPickColor = useIntelligentColorState()((state) => state.replaceIntelligentPickColor);
    const intelligentController = framework.getInstance(IntelligentColorPlugin);
    const intelligentColors = useIntelligentColorState()((state) => state.intelligentColors);
    const setSelectedData = useIntelligentColorState()((state) => state.setSelectedData);
    const setIsOpenIntelligentPickColor = useIntelligentColorState()((state) => state.setIsOpenIntelligentPickColor);

    // 绘制时候的画笔大小
    const drawPenSize = 15 / scale;
    const [mousePosition, setMousePosition] = useState<IPoint>({ x: -10000, y: 0 });
    const [color, setColor] = useState(DEFAULT_COLOR);
    const [inStage, setInStage] = useState<boolean>(false);

    useEffect(() => {
        const getImageData = async (position: any) => {
            if (!glCanvas) {
                const { canvas } = await enhanceGLBridge.getGLResult();
                glCanvas = canvas;
            }
            if (glCanvas) {
                const mainLayer = framework.getMainLayer(originIndex)!;
                const newPoint = convertMousePointToLayerPoint(mainLayer, position);
                const ctx = glCanvas.getContext('2d');
                const imagedata = ctx?.getImageData(newPoint.x, newPoint.y, 1, 1) as any;
                if (imagedata?.data) {
                    const rgba = Array.from(imagedata?.data).join(',');
                    return rgba;
                }
            }
            return '';
        };

        const downHandle = async (e) => {
            if (!isLeftButton(e)) return;
            if (isShowLoading) return;
            const mPoint = { x: e.x, y: e.y };
            const position = convertToLayerPoint(identity, mPoint, stageIndex);
            const inRect = checkPointInStage(identity, mPoint, stageIndex);
            if (inRect) {
                const color = await getImageData(position);
                setColor(color);
                const allColors = intelligentColors.map((v) => v.color);
                const index = allColors.findIndex((v) => v === color);
                if (index > -1) {
                    setSelectedData({ index, ...intelligentColors[index] });
                } else {
                    replaceIntelligentPickColor(color);
                }
                console.log('===>', false);
                setIsOpenIntelligentPickColor(false);
                intelligentController.renderIntelligentPreview(color);
                glCanvas = null;
            }
        };
        const moveHandle = async (e) => {
            // 设置鼠标位置
            const mPoint = { x: e.x, y: e.y };
            const position = convertToLayerPoint(identity, mPoint, stageIndex);
            const inRect = checkPointInStage(identity, mPoint, stageIndex);
            setMousePosition(position);
            if (!inRect) return setInStage(false);
            if (inRect) {
                const color = (await getImageData(position)) as any;
                setColor(color);
                setInStage(true);
            }
        };

        document.addEventListener('mousemove', moveHandle);
        document.addEventListener('mousedown', downHandle);
        return () => {
            document.removeEventListener('mousemove', moveHandle);
            document.removeEventListener('mousedown', downHandle);
        };
    }, [isShowLoading, drawPenSize, scale, intelligentColors]);

    return inStage ? (
        <div
            style={{
                width: penSize,
                height: penSize,
                transform: `translate(-50%, -50%)`,
                left: mousePosition.x,
                top: mousePosition.y,
            }}
            className="m-color-pick-pen"
        >
            <div className="m-color-pick-pen__color" style={{ borderColor: `rgba(${color})` }}></div>
        </div>
    ) : null;
});
